/**
 * Auth Context - глобальное состояние авторизации
 */

import React, {
    createContext,
    useState,
    useEffect,
    useCallback,
    useContext,
} from "react";
import { authService } from "../../api/services/authService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Инициализация при загрузке
    useEffect(() => {
        const initAuth = () => {
            const currentUser = authService.getCurrentUser();
            const authenticated = authService.isAuthenticated();

            setUser(currentUser);
            setIsAuthenticated(authenticated);
            setLoading(false);
        };

        initAuth();
    }, []);

    /**
     * Логин
     */
    const login = useCallback(async (credentials, twoFactorCode = null) => {
        setLoading(true);
        setError(null);

        try {
            if (twoFactorCode) {
                const errorMsg =
                    "Подтверждение 2FA выполняется отдельным запросом";
                setError(errorMsg);
                return { success: false, error: errorMsg };
            }

            const data = await authService.login(
                credentials.login,
                credentials.password,
            );

            if (data?.status === "error") {
                const errorMsg =
                    data.message || "Неверное имя пользователя или пароль!";
                setError(errorMsg);
                return { success: false, error: errorMsg };
            }

            if (data?.status === "gauth") {
                return { success: false, requires2FA: true };
            }

            if (data?.jwt) {
                authService.saveUserData(data);
                setUser(authService.getCurrentUser());
                setIsAuthenticated(true);
                return { success: true };
            }

            const errorMsg = data?.message || "Ошибка входа";
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } catch (err) {
            setError(err.message);
            return { success: false, error: err.message };
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Логаут
     */
    const logout = useCallback(() => {
        authService.clearUserData();
        setUser(null);
        setIsAuthenticated(false);
        setError(null);
    }, []);

    /**
     * Очистить ошибку
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const value = {
        user,
        isAuthenticated,
        loading,
        error,
        login,
        logout,
        clearError,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

/**
 * Hook для использования Auth Context
 */
export const useAuthContext = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuthContext must be used within AuthProvider");
    }
    return context;
};

export default AuthProvider;
